import {LayoutItem, sortLayoutItemsByRowCol} from 'react-grid-layout';

import type {HomepageCardDefaultLayouts, HomepageCardId} from '../HomepageCardDefinitions';

export type HomepageCardLayoutBreakpoint = 'narrow' | 'wide';

export const HOMEPAGE_CARD_LAYOUT_BREAKPOINTS = {
  wide: 800,
  narrow: 0,
} as const satisfies Record<HomepageCardLayoutBreakpoint, number>;

export const HOMEPAGE_CARD_LAYOUT_COLS = {
  wide: 2,
  narrow: 1,
} as const satisfies Record<HomepageCardLayoutBreakpoint, number>;

export interface HomepageCardLayoutDefinition {
  id: HomepageCardId;
  isFullWidth?: boolean;
}

export const HOMEPAGE_CARD_LAYOUT_ROW_HEIGHT_PX = 104;
export const HOMEPAGE_CARD_LAYOUT_EDIT_CARD_HEIGHT_ROWS = 1;
export const HOMEPAGE_CARD_LAYOUT_EDIT_ROW_SPACING_PX = 13;
export const HOMEPAGE_CARD_LAYOUT_COLUMN_SPACING_PX = 26;
export const HOMEPAGE_CARD_LAYOUT_VIEW_CARD_BOTTOM_SPACING_PX = 26;

type HomepageCardLayoutItem = LayoutItem & {i: HomepageCardId};
export type HomepageCardLayouts = Record<HomepageCardLayoutBreakpoint, ReadonlyArray<HomepageCardLayoutItem>>;
export type HomepageCardLayoutCardIds = Record<HomepageCardLayoutBreakpoint, Array<HomepageCardId>>;
export type HomepageCardLayoutCandidate =
  | Partial<Record<HomepageCardLayoutBreakpoint, ReadonlyArray<LayoutItem>>>
  | null
  | undefined;

interface HomepageCardLayoutFullWidthViewSection {
  sectionType: 'fullWidth';
  cardId: HomepageCardId;
}

interface HomepageCardLayoutColumnsViewSection {
  sectionType: 'columns';
  leftCardIds: Array<HomepageCardId>;
  rightCardIds: Array<HomepageCardId>;
}

export type HomepageCardLayoutViewSection =
  | HomepageCardLayoutFullWidthViewSection
  | HomepageCardLayoutColumnsViewSection;

function getCardLayoutDefinitionsById(
  cardLayoutDefinitions: ReadonlyArray<HomepageCardLayoutDefinition>
): Map<HomepageCardId, HomepageCardLayoutDefinition> {
  return new Map(cardLayoutDefinitions.map((definition) => [definition.id, definition]));
}

function isHomepageCardId(
  value: string,
  cardLayoutDefinitionsById: ReadonlyMap<HomepageCardId, HomepageCardLayoutDefinition>
): value is HomepageCardId {
  return cardLayoutDefinitionsById.has(value as HomepageCardId);
}

function getCardWidth(
  id: HomepageCardId,
  breakpoint: HomepageCardLayoutBreakpoint,
  cardLayoutDefinitionsById: ReadonlyMap<HomepageCardId, HomepageCardLayoutDefinition>
): 1 | 2 {
  if (breakpoint === 'narrow') {
    return 1;
  }
  return cardLayoutDefinitionsById.get(id)?.isFullWidth === true ? 2 : 1;
}

function getDefaultHomepageCardLayouts(
  cardLayoutDefinitions: ReadonlyArray<HomepageCardLayoutDefinition>,
  defaultLayouts: HomepageCardDefaultLayouts
): HomepageCardLayouts {
  const cardLayoutDefinitionsById = getCardLayoutDefinitionsById(cardLayoutDefinitions);

  return {
    wide: defaultLayouts.wide.map(({id, col, row}) => ({
      i: id,
      x: col,
      y: row,
      w: getCardWidth(id, 'wide', cardLayoutDefinitionsById),
      h: HOMEPAGE_CARD_LAYOUT_EDIT_CARD_HEIGHT_ROWS,
    })),
    narrow: defaultLayouts.narrow.map((id, index) => ({
      i: id,
      x: 0,
      y: index,
      w: 1,
      h: HOMEPAGE_CARD_LAYOUT_EDIT_CARD_HEIGHT_ROWS,
    })),
  };
}

function withHomepageCardLayoutConstraints(
  item: Pick<LayoutItem, 'i' | 'x' | 'y'> & Partial<LayoutItem>,
  breakpoint: HomepageCardLayoutBreakpoint,
  cardLayoutDefinitionsById: ReadonlyMap<HomepageCardId, HomepageCardLayoutDefinition>
): HomepageCardLayoutItem {
  const id = item.i as HomepageCardId;
  const width = getCardWidth(id, breakpoint, cardLayoutDefinitionsById);
  return {
    ...item,
    i: id,
    x: Math.max(0, item.x),
    y: Math.max(0, item.y),
    w: width,
    h: HOMEPAGE_CARD_LAYOUT_EDIT_CARD_HEIGHT_ROWS,
    minW: width,
    maxW: width,
    minH: HOMEPAGE_CARD_LAYOUT_EDIT_CARD_HEIGHT_ROWS,
    maxH: HOMEPAGE_CARD_LAYOUT_EDIT_CARD_HEIGHT_ROWS,
    isResizable: false,
  };
}

/**
 * Normalizes persisted preference data and react-grid-layout callback payloads against the current card registry.
 * This drops removed card IDs and reapplies fixed card width/height constraints before rendering or saving.
 */
export function sanitizeHomepageCardLayouts(
  layouts: HomepageCardLayoutCandidate,
  cardLayoutDefinitions: ReadonlyArray<HomepageCardLayoutDefinition>,
  defaultLayouts: HomepageCardDefaultLayouts
): HomepageCardLayouts {
  const cardLayoutDefinitionsById = getCardLayoutDefinitionsById(cardLayoutDefinitions);
  const defaultHomepageCardLayouts = getDefaultHomepageCardLayouts(cardLayoutDefinitions, defaultLayouts);
  const sanitizedLayouts: HomepageCardLayouts = {
    narrow: [],
    wide: [],
  };

  (Object.keys(HOMEPAGE_CARD_LAYOUT_BREAKPOINTS) as Array<HomepageCardLayoutBreakpoint>).forEach(
    (breakpoint) => {
      const defaultLayout = defaultHomepageCardLayouts[breakpoint];
      const candidateLayout = layouts?.[breakpoint] ?? null;

      if (candidateLayout == null) {
        sanitizedLayouts[breakpoint] = defaultLayout.map((item) =>
          withHomepageCardLayoutConstraints(item, breakpoint, cardLayoutDefinitionsById)
        );
        return;
      }

      const candidateItems = candidateLayout
        .filter((item) => isHomepageCardId(item.i, cardLayoutDefinitionsById))
        .map((item) => withHomepageCardLayoutConstraints(item, breakpoint, cardLayoutDefinitionsById));

      sanitizedLayouts[breakpoint] = candidateItems;
    }
  );

  return sanitizedLayouts;
}

export function filterHomepageCardLayouts(
  layouts: HomepageCardLayouts,
  visibleCardIds: ReadonlySet<HomepageCardId>,
  cardLayoutDefinitions: ReadonlyArray<HomepageCardLayoutDefinition>,
  defaultLayouts: HomepageCardDefaultLayouts
): HomepageCardLayouts {
  const sanitizedLayouts = sanitizeHomepageCardLayouts(layouts, cardLayoutDefinitions, defaultLayouts);
  return {
    narrow: sanitizedLayouts.narrow.filter((item) => visibleCardIds.has(item.i)),
    wide: sanitizedLayouts.wide.filter((item) => visibleCardIds.has(item.i)),
  };
}

export function getHomepageCardLayoutCardIds(layouts: HomepageCardLayouts): HomepageCardLayoutCardIds {
  return {
    narrow: getSortedHomepageCardLayoutItemIds(layouts.narrow),
    wide: getSortedHomepageCardLayoutItemIds(layouts.wide),
  };
}

function getSortedHomepageCardLayoutItemIds(
  layoutItems: ReadonlyArray<HomepageCardLayoutItem>
): Array<HomepageCardId> {
  return (sortLayoutItemsByRowCol(layoutItems) as Array<HomepageCardLayoutItem>).map((item) => item.i);
}

/**
 * Merges react-grid-layout change payloads into the saved layouts while preserving untouched breakpoints and cards.
 */
export function mergeHomepageCardLayoutChanges({
  currentLayouts,
  changedLayouts,
  changedCardIds,
  cardLayoutDefinitions,
  defaultLayouts,
}: {
  currentLayouts: HomepageCardLayouts;
  changedLayouts: HomepageCardLayoutCandidate;
  changedCardIds: ReadonlySet<HomepageCardId>;
  cardLayoutDefinitions: ReadonlyArray<HomepageCardLayoutDefinition>;
  defaultLayouts: HomepageCardDefaultLayouts;
}): HomepageCardLayouts {
  const sanitizedCurrentLayouts = sanitizeHomepageCardLayouts(
    currentLayouts,
    cardLayoutDefinitions,
    defaultLayouts
  );
  const sanitizedChangedLayouts = sanitizeHomepageCardLayouts(
    changedLayouts,
    cardLayoutDefinitions,
    defaultLayouts
  );

  const mergeBreakpoint = (
    breakpoint: HomepageCardLayoutBreakpoint
  ): ReadonlyArray<HomepageCardLayoutItem> => {
    if (changedLayouts?.[breakpoint] == null) {
      return sanitizedCurrentLayouts[breakpoint];
    }

    const changedItemsById = new Map(
      sanitizedChangedLayouts[breakpoint]
        .filter((item) => changedCardIds.has(item.i))
        .map((item) => [item.i, item])
    );

    return sanitizedCurrentLayouts[breakpoint].map((item) => changedItemsById.get(item.i) ?? item);
  };

  return {
    narrow: mergeBreakpoint('narrow'),
    wide: mergeBreakpoint('wide'),
  };
}

/**
 * Returns sanitized layout items in visual order, optionally scoped to the currently visible cards.
 */
function getSortedLayoutItems(
  layouts: HomepageCardLayouts,
  breakpoint: HomepageCardLayoutBreakpoint,
  cardLayoutDefinitions: ReadonlyArray<HomepageCardLayoutDefinition>,
  defaultLayouts: HomepageCardDefaultLayouts,
  visibleCardIds?: ReadonlySet<HomepageCardId>
): Array<HomepageCardLayoutItem> {
  return sortLayoutItemsByRowCol(
    sanitizeHomepageCardLayouts(layouts, cardLayoutDefinitions, defaultLayouts)[breakpoint].filter(
      (item) => visibleCardIds == null || visibleCardIds.has(item.i)
    )
  ) as Array<HomepageCardLayoutItem>;
}

/**
 * Converts the persisted grid layout into regular view-mode sections rendered without react-grid-layout.
 * Full-width cards render as their own rows, while adjacent one-column cards are grouped into
 * left/right column stacks until the next full-width card.
 */
export function getHomepageCardLayoutViewSections({
  layouts,
  visibleCardIds,
  breakpoint,
  cardLayoutDefinitions,
  defaultLayouts,
}: {
  layouts: HomepageCardLayouts;
  visibleCardIds: ReadonlySet<HomepageCardId>;
  breakpoint: HomepageCardLayoutBreakpoint;
  cardLayoutDefinitions: ReadonlyArray<HomepageCardLayoutDefinition>;
  defaultLayouts: HomepageCardDefaultLayouts;
}): Array<HomepageCardLayoutViewSection> {
  const sortedLayoutItems = getSortedLayoutItems(
    layouts,
    breakpoint,
    cardLayoutDefinitions,
    defaultLayouts,
    visibleCardIds
  );

  if (breakpoint === 'narrow') {
    return sortedLayoutItems.map((item) => ({sectionType: 'fullWidth', cardId: item.i}));
  }

  return sortedLayoutItems.reduce<Array<HomepageCardLayoutViewSection>>((sections, item) => {
    const nextSection: HomepageCardLayoutViewSection =
      item.w >= HOMEPAGE_CARD_LAYOUT_COLS.wide
        ? {sectionType: 'fullWidth', cardId: item.i}
        : {
            sectionType: 'columns',
            leftCardIds: item.x === 0 ? [item.i] : [],
            rightCardIds: item.x === 0 ? [] : [item.i],
          };
    const previousSection = sections[sections.length - 1];

    if (previousSection?.sectionType === 'columns' && nextSection.sectionType === 'columns') {
      return [
        ...sections.slice(0, -1),
        {
          sectionType: 'columns',
          leftCardIds: [...previousSection.leftCardIds, ...nextSection.leftCardIds],
          rightCardIds: [...previousSection.rightCardIds, ...nextSection.rightCardIds],
        },
      ];
    }

    return [...sections, nextSection];
  }, []);
}
